/* Lovable product page → Shopify. Vanilla custom elements, no dependencies.
   Every section includes this file; the guards below make repeat loads no-ops. */
(() => {
  if (window.__lvProductLoaded) return;
  window.__lvProductLoaded = true;

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- Cart (mirrors blocks/product-tier.liquid) ---------- */
  // XMLHttpRequest instead of fetch(): Appstle monkey-patches fetch() on this store.
  function addToCart(items) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${root}cart/add.js`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = () => {
        let data = {};
        try { data = JSON.parse(xhr.responseText); } catch (e) { /* empty body */ }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.description || data.message || 'Error'));
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(JSON.stringify({ items }));
    });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    const dialog = drawer && drawer.querySelector('dialog');
    if (drawer && typeof drawer.toggleDialog === 'function' && !(dialog && dialog.hasAttribute('open'))) drawer.toggleDialog();
    else if (!drawer) window.location.href = `${root}cart`;
  }

  function refreshCartDrawerAndOpen() {
    const items = document.querySelector('cart-items-component');
    const sectionId = (items && items.getAttribute('data-section-id')) || 'header';
    fetch(`${root}?sections=${sectionId}`)
      .then((r) => r.json())
      .then((sections) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = sections[sectionId] || '';
        const fresh = tmp.querySelector('cart-items-component');
        if (fresh && items) items.innerHTML = fresh.innerHTML;
        const count = tmp.querySelector('.cart-bubble__text-count');
        if (count) document.querySelectorAll('.cart-bubble__text-count').forEach((el) => { el.textContent = count.textContent.trim(); });
        document.querySelectorAll('cart-icon').forEach((icon) => icon.classList.add('header-actions__cart-icon--has-cart'));
      })
      .catch(() => {})
      .finally(openCartDrawer);
  }

  /* ---------- Horizontal scroller (ScrollRow / DoesForYou) ---------- */
  define('lv-scroller', class extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-track]');
      const factor = Number(this.dataset.factor || 0.6);
      const btns = this.querySelectorAll('[data-dir]');
      btns.forEach((btn) => {
        btn.addEventListener('click', () => {
          track.scrollBy({ left: Number(btn.dataset.dir) * track.clientWidth * factor, behavior: 'smooth' });
        });
      });
      // Hide an arrow when there is nothing more to scroll that way (and both when everything fits)
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        btns.forEach((btn) => {
          btn.hidden = Number(btn.dataset.dir) < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max - 2;
        });
      };
      track.addEventListener('scroll', update, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(update).observe(track);
      update();
    }
  });

  /* ---------- Sticky CTA bar: shows once the visitor has scrolled past the first screen ---------- */
  define('lv-sticky-cta', class extends HTMLElement {
    connectedCallback() {
      const offset = Number(this.dataset.offset || 600);
      const update = () => this.classList.toggle('is-visible', window.scrollY > offset);
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  });

  /* ---------- Side drawers (info panels) ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-lv-drawer]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.lvDrawer);
      if (dlg && dlg.showModal) dlg.showModal();
      return;
    }
    if (e.target.closest('[data-lv-close]')) {
      e.target.closest('dialog').close();
      return;
    }
    // click on the backdrop
    if (e.target.matches && e.target.matches('dialog.lv-drawer')) e.target.close();
  });

  /* ---------- "#buybox" links scroll to the N2 product-tier buy box ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="#buybox"]');
    if (!link) return;
    const target = document.getElementById('buybox') || document.querySelector('.product-buy-box');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  /* ---------- Ingredient cards: tap toggles, mouse hover previews ---------- */
  define('lv-toggle-cards', class extends HTMLElement {
    connectedCallback() {
      const cards = [...this.querySelectorAll('[data-card]')];
      const set = (card) => cards.forEach((c) => c.setAttribute('aria-pressed', String(c === card)));
      cards.forEach((card) => {
        card.addEventListener('click', () => set(card.getAttribute('aria-pressed') === 'true' ? null : card));
        card.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') set(card); });
        card.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') set(null); });
      });
    }
  });

  /* ---------- Stacked card deck (mobile "Formas de tomarlo") ---------- */
  define('lv-deck', class extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.active = 0;
      const stage = this.querySelector('[data-stage]');
      let startX = null;
      stage.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      stage.addEventListener('pointerup', (e) => {
        if (startX !== null && Math.abs(e.clientX - startX) > 30) this.go(this.active + (e.clientX < startX ? 1 : -1));
        startX = null;
      });
      this.cards.forEach((c, i) => c.addEventListener('click', () => { if (i !== this.active) this.go(i); }));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));
      this.render();
    }
    go(i) {
      this.active = Math.min(this.cards.length - 1, Math.max(0, i));
      this.render();
    }
    render() {
      // next cards peek on the right, passed cards tuck behind on the left
      this.cards.forEach((c, i) => {
        const off = i - this.active;
        const abs = Math.abs(off);
        c.style.transform = `translateX(${off * 26}px) scale(${1 - abs * 0.07})`;
        c.style.zIndex = String(10 - abs);
        c.style.opacity = abs > 2 ? '0' : String(1 - abs * 0.1);
        c.style.filter = abs ? 'brightness(0.94)' : '';
        c.setAttribute('aria-hidden', String(off !== 0));
      });
      this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === this.active)));
    }
  });

  /* ---------- Formula pillars ---------- */
  define('lv-formula', class extends HTMLElement {
    connectedCallback() {
      const pillars = [...this.querySelectorAll('[data-pillar]')];
      const panels = [...this.querySelectorAll('[data-panel]')];
      pillars.forEach((btn) =>
        btn.addEventListener('click', () => {
          pillars.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          panels.forEach((p) => {
            p.hidden = p.dataset.panel !== btn.dataset.pillar;
            if (!p.hidden) this.pick(p, p.querySelector('[data-ingredient]'));
          });
        }),
      );
      panels.forEach((panel) =>
        panel.querySelectorAll('[data-ingredient]').forEach((ing) => {
          ing.addEventListener('click', () => this.pick(panel, ing));
          ing.addEventListener('mouseenter', () => this.pick(panel, ing));
        }),
      );
    }
    pick(panel, ing) {
      if (!ing) return;
      panel.querySelectorAll('[data-ingredient]').forEach((x) => x.setAttribute('aria-pressed', String(x === ing)));
    }
  });

  /* ---------- Comparison slider (mobile) ---------- */
  define('lv-compare', class extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-track]');
      const range = this.querySelector('input[type="range"]');
      if (!track || !range) return;
      track.addEventListener('scroll', () => {
        const max = track.scrollWidth - track.clientWidth;
        range.value = max > 0 ? Math.round((track.scrollLeft / max) * 100) : 0;
      }, { passive: true });
      range.addEventListener('input', () => {
        track.scrollTo({ left: ((track.scrollWidth - track.clientWidth) * Number(range.value)) / 100 });
      });
    }
  });

  /* ---------- Community video card ---------- */
  define('lv-video-card', class extends HTMLElement {
    connectedCallback() {
      const v = this.querySelector('video');
      if (!v) return;
      const sync = () => {
        this.dataset.playing = String(!v.paused);
        this.dataset.muted = String(v.muted);
      };
      const play = () => v.play().catch(() => {}).finally(sync);
      const toggle = () => (v.paused ? play() : (v.pause(), sync()));
      v.addEventListener('click', toggle);
      v.addEventListener('play', sync);
      v.addEventListener('pause', sync);
      this.querySelector('[data-toggle]')?.addEventListener('click', toggle);
      this.querySelector('[data-restart]')?.addEventListener('click', () => { v.currentTime = 0; play(); });
      this.querySelector('[data-mute]')?.addEventListener('click', () => { v.muted = !v.muted; sync(); });
      // play only while visible (saves data and battery on mobile)
      new IntersectionObserver(([e]) => (e && e.isIntersecting ? play() : (v.pause(), sync())), { threshold: 0.6 }).observe(v);
      sync();
    }
  });

  /* ---------- Quick add (related products) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-lv-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      await addToCart([{ id: Number(btn.dataset.lvQuickAdd), quantity: 1 }]);
      refreshCartDrawerAndOpen();
    } catch (ex) {
      window.alert(ex.message);
    } finally {
      btn.disabled = false;
    }
  });
})();
